






























































































































































import {
  OrderUnits,
  DrivewealthAccountSummary,
  InstrumentDetail, DrivewealthAccountManagementType, ActivityHistoryType
} from "@/shared/types";
import {
  Vue,
  Component,
  Prop,
  Watch,
} from "@/shared/vuepack";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import RiskDisclaimer from "@/shared/components/investments/drivewealth/RiskDisclaimer.vue";
import SharePriceTable from "@/employee/components/investments/drivewealth/SharePriceTable.vue";
import {
  formatCurrency,
  roundCurrencyUSD
} from "@/shared/currency";
import _ from "lodash";
import * as RouteNames from "@/employee/route-names";
import * as SharedRoutes from "@/shared/route-names";
import { Validator } from "vee-validate";
import { useNotifierStore } from "@/shared/store/notifier";
import { useDrivewealthStore } from "@/shared/store/drivewealth";
import { useDrivewealthSweepStore } from "@/shared/store/drivewealthSweep";

const askToLiquidateIfSellingFraction = 0.9;
const askToLiquidateIfLeavingLessThan = 1; // USD
const MutualFund = "MUTUAL_FUND";

export interface RequestOrderData {
  shouldSellAll: boolean;
  amountUsd: number;
  isSubmitting: boolean;
  confirmedSweep: boolean;
}

@Component({
  components: {
    GenericLoadingSpinner,
    SharePriceTable,
    RiskDisclaimer,
  }
})
export default class RequestSellPage extends Vue {
  @Prop() symbol: string;
  @Prop({ required: false }) doubleConfirm;
  @Prop() accountId: number;
  data?: RequestOrderData = null;
  shouldSellAllConfirmation = true; // value for the doubleConfirm radio button
  loadedStockSymbol?: string = null;
  adjustToSellAll = false;

  formatCurrency = formatCurrency;

  $validator: Validator;
  notifierStore = useNotifierStore();

  drivewealthStore = useDrivewealthStore();
  drivewealthSweepStore = useDrivewealthSweepStore();

  created () {
    if (this.accountId) {
      this.drivewealthStore.getAccountSummaryV2({ force: true, username: null, partnerId: null, dwAccountId: this.accountId });
      this.drivewealthStore.loadPendingActivityByAccount({ force: true, accountId: this.accountId });
    } else {
      this.drivewealthStore.loadDefaultAccount();
      this.drivewealthStore.loadAccountSummary();
      this.drivewealthStore.loadPendingActivity({ force: true });
    }
    this.drivewealthStore.loadHsaBalanceAndThreshold({ force: true, accountId: this.accountId });
    this.drivewealthSweepStore.getSweep({});
    this.onCreatedAndLoaded();
  }

  get disableSellButton (): boolean {
    if (this.isLoaded) {
      const accountSummary = this.accountSummaryV2;
      let holdingBalance = 0;
      if (accountSummary.accountManagementType === DrivewealthAccountManagementType.RiaManaged) {
        holdingBalance = _.sumBy(accountSummary.holdings, "marketValue");
      } else {
        holdingBalance = accountSummary.holdings.find(x => x.stockSymbol === this.symbol).marketValue;
      }
      const pendingOrders = this.drivewealthStore.pendingActivityById(this.accountId).pendingOrders;
      const pendingSell = pendingOrders.filter(x => x.type === ActivityHistoryType.Sell && x.stockSymbol === this.symbol).reduce((sum, current) => sum + current.dollarAmount, 0);
      const formattedHoldingBalance = (Number(holdingBalance.toFixed(2)) * 100).toFixed(0);
      const formattedPendingSell = (Number(pendingSell.toFixed(2)) * 100).toFixed(0);

      return this.data.amountUsd > (Number(formattedHoldingBalance) - Number(formattedPendingSell)) / 100;
    }

    return false;
  }

  get accountSummaryV2 (): DrivewealthAccountSummary {
    if (this.accountId) {
      return this.drivewealthStore.accountSummaryById(this.accountId);
    }
    return this.drivewealthStore.accountSummary;
  }

  get isLoading () {
    return !this.isLoaded;
  }

  get isLoaded () {
    const accountSummaryV2Loaded = this.accountId ?
      this.drivewealthStore.accountSummaryV2Loaded(this.accountId) :
      this.drivewealthStore.accountSummaryLoaded;

    const instrumentDataLoaded = this.isManagedAccount ? true : this.drivewealthStore.instrumentDataIsLoaded(this.symbol);

    return this.drivewealthStore.drivewealthLoaded &&
      this.drivewealthStore.isHsaBalanceAndThresholdLoaded(this.accountId) &&
      this.drivewealthSweepStore.sweepLoaded &&
      accountSummaryV2Loaded &&
      !!this.data &&
      instrumentDataLoaded;
  }

  get heading () {
    return this.drivewealthStore.instrumentHeading(this.symbol, this.accountId);
  }

  get subheading () {
    return this.drivewealthStore.instrumentSubheading(this.symbol, this.accountId);
  }

  get isManagedAccount () {
    return this.drivewealthStore.isAccountManagementType(
      DrivewealthAccountManagementType.RiaManaged, this.accountId);
  }

  @Watch("symbol")
  getData () {
    if (this.data && this.loadedStockSymbol !== this.symbol) {
      this.data = null; // the symbol changed, gotta start over!
      this.onCreatedAndLoaded(); // N.B. Setting data = null stops this from being infinite loop
    }

    this.loadedStockSymbol = this.symbol;
    this.data = {
      shouldSellAll: false,
      amountUsd: 0,
      isSubmitting: false,
      confirmedSweep: false,
    };
  }

  @Watch("drivewealthStore.drivewealthLoaded")
  onCreatedAndLoaded () {
    if (!this.drivewealthStore.drivewealthLoaded) {
      return;
    }

    if (!this.isManagedAccount) {
      this.drivewealthStore.loadInstrumentData(this.symbol, this.accountId);
    }

    this.getData();
  }

  get hasNotConfirmedSweep () {
    return this.drivewealthSweepStore.sweepIsActive && !this.data.confirmedSweep;
  }

  get pendingLiquidation () {
    return this.isManagedAccount && this.drivewealthStore.hasPendingManagedLiquidation;
  }

  get disabledAll () {
    return this.hasNotConfirmedSweep || this.pendingLiquidation;
  }

  get amountHeldUsd () {
    if (this.isManagedAccount) {
      return this.drivewealthStore.amountHeld(this.symbol, this.accountId) + this.drivewealthStore.cashAvailableForWithdrawal(this.accountId);
    }

    return this.drivewealthStore.amountHeld(this.symbol, this.accountId);
  }

  get sellAmountEqualsAmountHeld () {
    return this.amountHeldUsd === roundCurrencyUSD(this.data.amountUsd);
  }

  get sellAmountToAmountHeldPercentage (): number {
    return Math.ceil(roundCurrencyUSD(this.data.amountUsd) / this.amountHeldUsd);
  }

  get liquidate () {
    if (this.doubleConfirm) {
      return this.shouldSellAllConfirmation;
    }

    return this.data.shouldSellAll || this.sellAmountEqualsAmountHeld;
  }

  get shouldSellAll () {
    return this.data.shouldSellAll;
  }

  set shouldSellAll (value) {
    if (value && value !== this.data.shouldSellAll) {
      this.data.amountUsd = this.amountHeldUsd;
    }
    this.data.shouldSellAll = value;
  }

  get instrumentDetail (): InstrumentDetail {
    return this.drivewealthStore.instrumentDetailBySymbol(this.symbol);
  }

  @Watch("data.shouldSellAll")
  resetMutualFundAmountValidation (newValue): void {
    if (!this.instrumentDetail || (this.instrumentDetail.fundType !== MutualFund)) {
      return;
    }

    if (newValue === false) {
      this.adjustToSellAll = false;
    }
  }

  @Watch("data.amountUsd")
  checkMutualFundAmountValidation (): void {
    if (!this.instrumentDetail || (this.instrumentDetail.fundType !== MutualFund)) {
      return;
    }

    const fractionSelling = this.data.amountUsd / this.amountHeldUsd;
    if (fractionSelling > askToLiquidateIfSellingFraction && fractionSelling < 1) {
      this.data.amountUsd = this.amountHeldUsd;
      this.$nextTick(() => {
        this.adjustToSellAll = true;
        this.data.shouldSellAll = true;
      });
    }
  }

  get units (): OrderUnits {
    return this.liquidate ? "SHARES" : "USD";
  }

  get amount () {
    return this.liquidate ? this.drivewealthStore.availableForTradingQty(this.symbol, this.accountId) : this.data.amountUsd;
  }

  get sweepRoute () {
    if (this.accountId) {
      return RouteNames.DrivewealthSweepV2;
    }

    return RouteNames.DrivewealthSweep;
  }

  onAcknowledgedSweepIsActive () {
    this.data.confirmedSweep = true;
  }

  navigateBack () {
    window.history.length > 1 ? this.$router.go(-1) : this.$router.push("/");
  }

  navigateOnSaleRequested () {
    if (this.accountId) {
      this.$router.push({
        name: SharedRoutes.DrivewealthInvestmentAccountV2,
        params: { accountId: `${this.accountId}`},
        query: { force: "true" }
      });
    } else {
      this.$router.push({ name: RouteNames.InvestmentAccountInfo, query: { force: "true" } });
    }
  }

  get amountValidator () {
    return {
      required: true,
      decimal: 2,
      min_value_usd: 0.01,
      max_value_usd: this.amountHeldUsd
    };
  }

  onConfirm () {
    if (this.data.isSubmitting) {
      return;
    }

    this.$validator.validateAll().then(result => {
      if (!result) {
        return;
      }

      this.data.isSubmitting = true;
      this.onConfirmAndValidated();
    });
  }

  onCancel () {
    this.navigateBack();
  }

  onConfirmAndValidated () {
    if (!this.liquidate) {
      const amountLeft = this.amountHeldUsd - this.data.amountUsd;
      const fractionSelling = this.data.amountUsd / this.amountHeldUsd;
      if (amountLeft < askToLiquidateIfLeavingLessThan || fractionSelling > askToLiquidateIfSellingFraction) {
        if (this.accountId) {
          this.$router.push({
            name: RouteNames.DrivewealthSellInstrumentConfirmV2,
            params: { symbol: this.symbol, accountId: `${this.accountId}` }
          });
        } else {
          this.$router.push({
            name: RouteNames.DrivewealthSellInstrumentConfirm,
            params: { symbol: this.symbol }
          });
        }

        this.data.isSubmitting = false;
        return;
      }
    }

    this.sendRequest();
  }

  onConfirmSubmit () {
    if (this.data.isSubmitting) {
      return;
    }

    this.$validator.validateAll().then(result => {
      if (!result) {
        return;
      }

      this.data.isSubmitting = true;
      this.sendRequest();
    });

  }

  sendRequest () {
    let orderPromise: Promise<any>;

    if (this.isManagedAccount) {
      const sellAmount = this.liquidate ? undefined : this.data.amountUsd;
      if (this.accountId) {
        orderPromise = this.drivewealthStore.createManagedRedemptionByAccount({
          data: {
            amount: sellAmount,
            isLiquidation: this.liquidate
          },
          accountId: this.accountId
        });
      } else {
        orderPromise = this.drivewealthStore.createManagedRedemption({
          amount: sellAmount,
          isLiquidation: this.liquidate
        });
      }
    } else {
      if (this.accountId) {
        orderPromise = this.drivewealthStore.createOrderRequestByAccount({
          data: {
            side: "SELL",
            units: this.units,
            stockSymbol: this.symbol,
            amount: this.amount
          },
          accountId: this.accountId
        });
      } else {
        orderPromise = this.drivewealthStore.createOrderRequest({
          side: "SELL",
          units: this.units,
          stockSymbol: this.symbol,
          amount: this.amount
        });
      }
    }

    orderPromise.then(() => {
      const message = (this.liquidate
        ? `Sale of all ${this.symbol} holdings sent`
        : `Sale of ${formatCurrency(this.data.amountUsd)} ${this.symbol} sent`)
        + "<div>It may take up to 2 minutes for the order to display.</div>";
      this.notifierStore.setSuccess(message);
      this.navigateOnSaleRequested();
    }).finally(() => {
      this.data.isSubmitting = false;
    });
  }
}
